import React, { useEffect, useState } from 'react';
import {
	Alert,
	Button,
	CircularProgress,
	IconButton,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { DataGrid, esES } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import {
	handleConfirmModal,
	handleError,
	rowStyle,
	whichDivison,
} from '../../helpers/utils';
import { useDispatch, useSelector } from 'react-redux';
import { formatDateTimeMX } from '../../helpers/hours';
import { deleteClient, getClients } from '../../actions/client';
import { SearcherClient } from '../searcher/SearcherClient';

const defaultEndpoint = '/api/client/?ordering=-created_at&page_size=5';
const searchIconStyle = { color: '#143f6c', cursor: 'pointer' };

// Clients table
export const ClientsTable = ({ data = [] }) => {
	// Hook for navigation
	const navigate = useNavigate();

	// Dispatch
	const dispatch = useDispatch();

	const { user_type } = useSelector((state) => state.user);

	// Show searcher or not
	const [showSearcher, setShowSearcher] = useState(false);

	// Loading state
	const [isLoading, setLoading] = useState(false);

	// Rows
	const [rowsState, setRowsState] = useState({
		isLoading: false,
		data: [],
		total: 0,
		page: 1,
		lastPage: 1,
		pageSize: 5,
		next: defaultEndpoint,
		previous: null,
	});

	const generateRows = async (newData = false) => {
		let newClients = [];
		let total = 0;
		let data = null;

		const moveForward = rowsState.page >= rowsState.lastPage;

		setLoading(true);

		if (newData) {
			newClients = newData.results;
			total = newData.count;
		} else {
			data = (
				await getClients(
					newData === null
						? defaultEndpoint
						: moveForward
						? rowsState.next
						: rowsState.previous
				)
			).data;
			newClients = data.results;
			total = data.count;
		}

		if (newClients.length === 0) {
			setRowsState((old) => ({
				...old,
				isLoading: false,
				data: [],
				total: 0,
				next: data?.next,
				previous: data?.previous,
			}));
		} else {
			const res = newClients.map((item) => ({
				...item,
				company_name: item.company_name.toUpperCase(),
				division: whichDivison(item.division),
				created_at: formatDateTimeMX(item.created_at),
			}));

			setRowsState((old) => ({
				...old,
				isLoading: false,
				data: res,
				total: total,
				pageSize: newData === null ? 5 : old.pageSize,
				next: data?.next,
				previous: data?.previous,
			}));
		}

		setLoading(false);
	};

	// Handle delete user with modal confirmation
	const handleDeleteUser = (userId) => {
		handleConfirmModal(
			'¡Precaución!',
			'¿Estás seguro de eliminar permanentemente al cliente del sistema?',
			'warning',
			'Sí, eliminar'
		)
			.then(({ isConfirmed }) => {
				if (isConfirmed) {
					dispatch(deleteClient(userId));
				}
			})
			.catch((error) => handleError(error));
	};

	useEffect(() => {
		generateRows();
	}, [rowsState.page, rowsState.pageSize]);

	// Define columns
	const columns = [
		{
			field: 'col8',
			headerName: 'Acciones',
			width: 150,
			// Add actions to each case
			renderCell: ({ row }) => (
				<>
					<Tooltip title='Ver perfil de cliente'>
						<IconButton
							style={{ color: '#143f6c' }}
							aria-label='profile'
							color='primary'
							onClick={() => {
								navigate(`/dashboard/cliente/${row.id}`);
							}}
						>
							<PersonIcon />
						</IconButton>
					</Tooltip>
					{[1, 2, 5].includes(user_type.id) && (
						<Tooltip title='Editar cliente'>
							<IconButton
								style={{ color: '#143f6c' }}
								aria-label='profile'
								color='primary'
								onClick={() => {
									navigate('/dashboard/editar-cliente', {
										state: { clientData: row },
									});
								}}
							>
								<EditIcon />
							</IconButton>
						</Tooltip>
					)}
					{user_type.id === 2 && (
						<Tooltip title='Eliminar cliente'>
							<IconButton
								style={{ color: '#143f6c' }}
								aria-label='delete'
								color='primary'
								onClick={() => handleDeleteUser(row.id)}
							>
								<DeleteIcon />
							</IconButton>
						</Tooltip>
					)}
				</>
			),
		},
		{
			field: 'id',
			headerName: 'ID',
			width: 50,
			renderCell: ({ row }) => (
				<Tooltip title={row.id}>
					<span className='table-cell-trucate'>{row.id}</span>
				</Tooltip>
			),
		},
		{
			field: 'company_name',
			headerName: 'Razón social',
			width: 250,
			renderCell: ({ row }) => (
				<Tooltip title={row.company_name}>
					<span className='table-cell-trucate'>
						{row.company_name}
					</span>
				</Tooltip>
			),
		},
		{
			field: 'consultant',
			headerName: 'Asesor',
			width: 200,
			renderCell: ({ row }) => (
				<Tooltip title={row.consultant}>
					<span className='table-cell-trucate'>{row.consultant}</span>
				</Tooltip>
			),
		},
		{
			field: 'division',
			headerName: 'División',
			width: 100,
			renderCell: ({ row }) => (
				<Tooltip title={row.division}>
					<span className='table-cell-trucate'>{row.division}</span>
				</Tooltip>
			),
		},
		{
			field: 'city',
			headerName: 'Ciudad',
			width: 130,
			renderCell: ({ row }) => (
				<Tooltip title={row.city}>
					<span className='table-cell-trucate'>{row.city}</span>
				</Tooltip>
			),
		},
		{
			field: 'rfc',
			headerName: 'RFC',
			width: 140,
			renderCell: ({ row }) => (
				<Tooltip title={row.rfc}>
					<span className='table-cell-trucate'>{row.rfc}</span>
				</Tooltip>
			),
		},
		{
			field: 'bank',
			headerName: 'Banco',
			width: 130,
			renderCell: ({ row }) => (
				<Tooltip title={row.bank}>
					<span className='table-cell-trucate'>{row.bank}</span>
				</Tooltip>
			),
		},
		{
			field: 'created_at',
			headerName: 'Creado',
			width: 150,
			renderCell: ({ row }) => (
				<Tooltip title={row.created_at}>
					<span className='table-cell-trucate'>{row.created_at}</span>
				</Tooltip>
			),
		},
	];

	return (
		<div
			style={{
				padding: '20px',
			}}
		>
			<Stack
				direction='row'
				alignItems='flex-end'
				justifyContent='space-between'
				style={{
					border: '1px solid white',
					padding: '20px 20px 0 0',
					margin: '0 0 40px 0',
					width: '100%',
				}}
			>
				<Typography variant='h4' gutterBottom>
					Clientes
				</Typography>
				<Button
					variant='contained'
					style={{
						backgroundColor: '#143f6c',
					}}
					onClick={() => {
						navigate('/dashboard/nuevo-cliente');
					}}
				>
					Nuevo cliente
				</Button>
			</Stack>

			<hr />

			<Typography
				variant='h6'
				gutterBottom
				style={{ ...rowStyle, margin: '20px 0 10px 0' }}
			>
				Búsqueda global{' '}
				<div
					onClick={() => setShowSearcher(!showSearcher)}
					style={{ margin: '0 10px' }}
				>
					{showSearcher ? (
						<SearchOffIcon style={searchIconStyle} />
					) : (
						<SearchIcon style={searchIconStyle} />
					)}
				</div>
			</Typography>

			{/* Order searcher */}
			{showSearcher && (
				<SearcherClient
					generateRows={generateRows}
					handleClose={() => setShowSearcher(false)}
					rowsState={rowsState}
					setRowsState={setRowsState}
				/>
			)}

			{isLoading ? (
				<center style={{ padding: '20%' }}>
					<CircularProgress />
				</center>
			) : (
				<>
					{/* Datagrid table */}
					{rowsState.data.length > 0 ? (
						<DataGrid
							rows={rowsState.data}
							rowCount={rowsState.total}
							loading={rowsState.isLoading}
							rowsPerPageOptions={[5, 10, 20]}
							pagination
							autoHeight
							page={rowsState.page - 1}
							pageSize={rowsState.pageSize}
							paginationMode='server'
							onPageChange={(newPage) => {
								setRowsState((old) => ({
									...old,
									page: newPage + 1,
									lastPage: old.page,
								}));
							}}
							onPageSizeChange={(newPage) => {
								if (showSearcher) {
									setRowsState((old) => ({
										...old,
										pageSize: newPage,
										next: `${old.current}&page_size=${newPage}`,
										page: 1,
										lastPage: 1,
									}));
								} else {
									setRowsState((old) => ({
										...old,
										pageSize: newPage,
										next: `/api/client/?ordering=-created_at&page_size=${newPage}`,
										page: 1,
										lastPage: 1,
									}));
								}
							}}
							columns={columns}
							localeText={
								esES.components.MuiDataGrid.defaultProps
									.localeText
							}
						/>
					) : (
						<Alert severity='info'>No hay ningún cliente</Alert>
					)}
				</>
			)}
		</div>
	);
};
